import { useQuery } from "@apollo/client";
import { PERSON_BY_ID_QUERY } from "../../../../../api/person/queries";
import { personTilsynerklaeringForm } from "../../../../../config/form/person/personTilsynerklaeringFormConfig";
import { getHeaderActiveColumns, TD } from "../../../../../util/table";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import ButtonDownload from "../../../../ButtonDownload";
import DocumentIcon from "../../../../icons/DocumentIcon";
import TableBasic, { TableRow } from "../../../../Table/TableBasic";
import DocumentModal from "../../../../Modal/modals/DocumentModal";
import IngenRegistreringer from "../IngenRegistreringer";
import OpretNyButtonBlock from "../OpretNyButtonBlock";
import {
  PersonDto,
  PersonTilsynserklaering,
} from "../../../../../interfaces/schema";
import { PersonTilsynerklaeringField } from "../../../../../interfaces/personer";
import { IColumn, SortBy } from "../../../../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import { useContext } from "react";
import { UserQueryContext } from "../../../../../context/UserContext";
import TLinkButton from "../../../../popover/TLinkButton";
import { orderBy } from "../../../../../util/orderBy";

export default function TilsynsErklaering({
  columns,
  defaultColumns,
  invalidateData,
  personId,
  personTilsynsErklaering,
  setActiveColumns,
}: {
  personId: string;
  invalidateData: InvalidateDataFunction<any>;
  personTilsynsErklaering: PersonTilsynserklaering[];
  defaultColumns: IColumn<PersonTilsynerklaeringField>[];
  columns: IColumn<PersonTilsynerklaeringField>[];
  setActiveColumns: React.Dispatch<
    React.SetStateAction<PersonTilsynerklaeringField[]>
  >;
}) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const isAuth = hasPermission(user, "TilsynWrite");
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personTilsynsErklaeringSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );

  const { data } = useQuery<{
    person: PersonDto;
  }>(PERSON_BY_ID_QUERY(), {
    variables: { id: personId },
  });

  // event handlers
  function downloadHandler() {
    tableToXLSX(columns, personTilsynsErklaering, {
      fileName: `tilsynserklæring-for-${data?.person.kundeNr}`,
      additionalData: {
        kundenr: data?.person.kundeNr,
      },
    });
  }

  if (!personTilsynsErklaering.length) {
    return (
      <IngenRegistreringer
        editConfig={
          isAuth ? personTilsynerklaeringForm(personId, undefined) : undefined
        }
        invalidateData={invalidateData}
      />
    );
  }
  return (
    <>
      <ButtonDownload onClick={downloadHandler} />
      <TableBasic
        columns={columns}
        setActiveColumns={setActiveColumns}
        defaultColumns={defaultColumns}
        headerRow={
          <>
            {getHeaderActiveColumns(columns, {
              defaultSortBy,
              setSortBy,
              sortBy,
            })}
            <th scope="col">Link</th>
            <th scope="col">Erklæring</th>
          </>
        }
      >
        {orderBy(
          personTilsynsErklaering,
          sortBy.map((ds) => ds.field),
          sortBy.map((ds) => ds.direction),
        ).map((erkl) => {
          const editConfig = personTilsynerklaeringForm(
            personId,
            erkl.id,
            erkl,
          );
          return (
            <TableRow
              key={erkl.id}
              editConfig={isAuth ? editConfig : undefined}
              invalidateData={invalidateData}
            >
              <>
                {columns
                  .filter((col) => col.active)
                  .map((col) => {
                    return <TD key={col.field} col={col} rowData={erkl} />;
                  })}
              </>
              <td>
                {!erkl.erklModtaget &&
                  erkl.tilsynserklaeringStatusNavigation?.nr ===
                    "FRIST_ORDINAER" && (
                    <TLinkButton
                      id={erkl.id}
                      erklaeringtype="tilsynserklaering"
                    />
                  )}
              </td>
              <td>
                {editConfig.getDocument && (
                  <DocumentModal
                    getDocument={editConfig.getDocument}
                    downloadLink={editConfig.downloadLink}
                    title={
                      <>
                        <DocumentIcon height={20} />
                        Tilsynserklæring
                      </>
                    }
                    buttonContents={[
                      <DocumentIcon key={1} />,
                      <span key={2}>Vis erklæring</span>,
                    ]}
                    contentProps={{
                      className: "c-document c-document--tilsynserklaering",
                    }}
                  />
                )}
              </td>
            </TableRow>
          );
        })}
      </TableBasic>
      <OpretNyButtonBlock
        editConfig={
          isAuth ? personTilsynerklaeringForm(personId, undefined) : undefined
        }
        invalidateData={invalidateData}
      />
    </>
  );
}
const defaultSortBy: SortBy[] = [
  {
    direction: "desc",
    field: "erklAar",
  },
];
